<template>
  <page-template :is-loading="isLoading" :data-exists="quarantinedData !== null">
    <template #pageHeadline>
      {{ $t('quarantinedList.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="quarantinedData"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="quarantined-table"
        :default-sort-column-index="3"
        :row-buttons="rowButtons"
        :show-checkbox-column="true"
        :show-delete-selected-button="true"
        :show-download-button="false"
        :show-new-button="false"
        :show-filter-row="true"
        @delete-row-button-clicked="deleteQuarantinedElement"
        @delete-selected="deleteSelectedRows"
      >
        <template v-if="showFilteringIpBlockingSlot" #offloadIpBlockingSlot="cellProps">
          <messages-badge
            v-if="isOffloadIpBlockingEnabled(cellProps.row)"
            :message="$t('quarantinedList.offloadModeOn')"
            classes="text-small-padding"
            color-variant="full-info"
          >
          </messages-badge>
        </template>
      </rbz-table>
    </template>
  </page-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Action, DynamicRule, QuarantinedEntry } from '@/types';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  shouldUseMockData,
  getQuarantinedMockData,
  getDynamicRulesMockData,
  getActionsMockData,
} from '@/helpers/MockDataUtils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import _ from 'lodash';
import PageTemplate from '@/views/PageTemplate.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import MessagesBadge from 'ui-component-library/rbz/components/MessagesBadge.vue';
import { ENV } from '@/constants';

export default defineComponent({
  name: 'QuarantinedList',
  components: {
    MessagesBadge,
    PageTemplate,
    RbzTable,
  },
  data() {
    return {
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: (item: QuarantinedEntry) => {
            return `/${this.selectedBranch}/dynamic-rules/config/${item.rule_id}`;
          },
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      quarantinedData: null as QuarantinedEntry[],
      dynamicRules: [] as Partial<DynamicRule>[],
      actions: [] as Action[],
      pageLoadingCounter: null,
      publicCloudProviders: ['gcp', 'aws'],
      provider: ENV.VITE_LOAD_BALANCER_PROVIDER,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    showFilteringIpBlockingSlot(): boolean {
      return this.provider && !this.publicCloudProviders.includes(this.provider);
    },

    columns(): RbzTableColumnOptions[] {
      const columnsArray = [
        {
          id: 'target',
          title: this.$t('quarantinedList.keyParameter'),
          fieldNames: ['target'],
          isSortable: true,
          isSearchable: true,
          displayFunction: (item: QuarantinedEntry) => {
            if (item['target'] === 'ip') {
              return this.$t('common.ipAddress');
            } else {
              return item['target'];
            }
          },
          classes: 'width-100px',
          cellContentClasses: 'multi-line text-small-padding',
          autoFocus: true,
        },
        {
          id: 'value',
          title: this.$t('quarantinedList.value'),
          fieldNames: ['value'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-120px',
          cellContentClasses: 'multi-line text-small-padding',
        },
        {
          id: 'count',
          title: this.$t('quarantinedList.count'),
          fieldNames: ['count'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          displayFunction: (item: QuarantinedEntry) => {
            return this.isOffloadIpBlockingEnabled(item) ? 'NA' : item.count;
          },
          classes: 'width-80px',
          cellContentClasses: 'text-small-padding',
        },
        {
          id: 'timestamp',
          title: this.$t('quarantinedList.firstAdded'),
          fieldNames: ['timestamp'],
          isSortByOriginalValue: true,
          displayFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['timestamp']);
            const adjustedDate = DateTimeUtils.adjustDateToTimezone(date);
            return DateTimeUtils.isoToNowCuriefenseFormat(adjustedDate);
          },
          tooltipFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['timestamp']);
            return `${DateTimeUtils.isoToNowFullCuriefenseFormat(date)} UTC`;
          },
          tooltipFunctionAlwaysDisplayed: true,
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line text-small-padding',
        },
        {
          id: 'last-seen',
          title: this.$t('quarantinedList.lastSeen'),
          fieldNames: ['last_seen'],
          isSortByOriginalValue: true,
          displayFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['last_seen'] * 1000);
            return DateTimeUtils.isoToNowCuriefenseFormat(date);
          },
          tooltipFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['last_seen'] * 1000);
            const adjustedDate = DateTimeUtils.adjustDateToUTC(date);
            return `${DateTimeUtils.isoToNowFullCuriefenseFormat(adjustedDate)} UTC`;
          },
          tooltipFunctionAlwaysDisplayed: true,
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line text-small-padding',
        },
        {
          id: 'expires',
          title: this.$t('quarantinedList.expires'),
          fieldNames: ['expires'],
          isSortByOriginalValue: true,
          displayFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['expires'] * 1000);
            return DateTimeUtils.isoToNowCuriefenseFormat(date);
          },
          tooltipFunction: (item: QuarantinedEntry) => {
            const date = new Date(item['expires'] * 1000);
            const adjustedDate = DateTimeUtils.adjustDateToUTC(date);
            return `${DateTimeUtils.isoToNowFullCuriefenseFormat(adjustedDate)} UTC`;
          },
          tooltipFunctionAlwaysDisplayed: true,
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line text-small-padding',
        },
        {
          id: 'dynamic-rule-id',
          title: this.$t('quarantinedList.dynamicRule'),
          fieldNames: ['rule_id'],
          displayFunction: (item: QuarantinedEntry) => {
            const matchingDynamicRule: Partial<DynamicRule> = _.find(
              this.dynamicRules,
              (dynamicRule) => {
                return dynamicRule.id === item.rule_id;
              }
            );
            return matchingDynamicRule?.name.trim() || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
          cellContentClasses: 'multi-line text-small-padding',
        },
        {
          id: 'action',
          title: this.$t('quarantinedList.action'),
          fieldNames: ['action'],
          displayFunction: (item: QuarantinedEntry) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px action-cell',
          cellContentClasses: 'ellipsis text-small-padding',
        },
      ];
      if (this.showFilteringIpBlockingSlot) {
        columnsArray.splice(2, 0, {
          id: 'offload_ip_filtering',
          title: this.$t('quarantinedList.offloadIpBlocking'),
          fieldNames: ['offload_ip_filtering'],
          displaySlotName: 'offloadIpBlockingSlot',
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
          cellContentClasses: 'text-small-padding',
        });
      }
      return columnsArray;
    },
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Quarantined') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          const dynamicRulePromise = this.loadDynamicRules();
          const actionPromise = this.loadActions();
          await Promise.all([dynamicRulePromise, actionPromise]);
          await this.loadQuarantinedData();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadDynamicRules() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getDynamicRulesMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/dynamic-rules`,
          config: { headers: { 'x-fields': 'id, name, ttl' } },
        });
      }
      this.dynamicRules = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getActionsMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/actions`,
          config: { headers: { 'x-fields': 'id, name' } },
        });
      }
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadQuarantinedData() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getQuarantinedMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `data/quarantined?config=${this.selectedBranch}`,
          config: {
            headers: {
              provider: ENV.VITE_STATE_DB_PROVIDER,
            },
          },
        });
      }

      this.quarantinedData = _.map(
        response?.data?.data?.results,
        (quarantinedEntry: QuarantinedEntry) => {
          const matchingDynamicRule: Partial<DynamicRule> = _.find(
            this.dynamicRules,
            (dynamicRule) => {
              return dynamicRule.id === quarantinedEntry.rule_id;
            }
          );
          const lastSeen = quarantinedEntry['last_seen'] ? quarantinedEntry['last_seen'] : 0;
          const ttl = matchingDynamicRule?.ttl ? matchingDynamicRule.ttl : 0;
          return {
            ...quarantinedEntry,
            id: quarantinedEntry._id,
            expires: lastSeen + ttl,
          };
        }
      );
      this.setPageLoadingStatus(false);
    },

    async deleteQuarantinedElement(quarantinedElement: QuarantinedEntry) {
      this.setPageLoadingStatus(true);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: 'data/quarantined',
        config: {
          headers: {
            provider: ENV.VITE_STATE_DB_PROVIDER,
          },
          data: {
            config: this.selectedBranch,
            ids: [quarantinedElement.id],
          },
        },
      });
      await this.loadQuarantinedData();
      this.setPageLoadingStatus(false);
    },

    async deleteSelectedRows(selectedArray: string[]) {
      this.setPageLoadingStatus(true);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: 'data/quarantined',
        config: {
          headers: {
            provider: ENV.VITE_STATE_DB_PROVIDER,
          },
          data: {
            config: this.selectedBranch,
            ids: selectedArray,
          },
        },
      });
      await this.loadQuarantinedData();
      this.setPageLoadingStatus(false);
    },

    isOffloadIpBlockingEnabled(quarantinedElement: QuarantinedEntry) {
      const matchingDynamicRule: Partial<DynamicRule> = _.find(this.dynamicRules, (dynamicRule) => {
        return dynamicRule.id === quarantinedElement.rule_id;
      });
      return matchingDynamicRule?.offload_ip_filtering;
    },
  },
});
</script>

<style scoped lang="scss">
:deep(.rbz-table .multi-line) {
  -webkit-line-clamp: 2;
  word-break: break-all;
}
</style>
